import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { StyledButton } from '@selfserve/react-ui';
import { ProductTypes, routesPaths, productType } from '@selfserve/react-utils';

import { IUpdateFormikReturn } from '../types';
import { LocationCloneState, Package, Plan } from '../data';

const StyledContainer = styled('div')`
  width: calc(100% + 64px);
  margin-left: -64px;
  display: flex;
  padding: 24px 0 32px 64px;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #fff;
`;

const StyledButtonsContainer = styled('div')`
  button + button {
    margin-left: 16px;
  }
`;

const StyledBackLinkContainer = styled('div')`
  display: flex;
  white-space: break-spaces;
  display: flex;
  align-items: flex-end;
`;

const StyledBackLink = styled('h2')`
  position: relative;
  cursor: pointer;
  font-size: 32px;
  font-weight: 700;
  line-height: 36px;
  margin: 0;
  &::before {
    position: absolute;
    content: '';
    background: no-repeat url('assets/arrow-left.svg') left center;
    width: 30px;
    height: 100%;
    left: -28px;
    top: 2px;
  }
`;

const StyledPackageName = styled('h4')`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
`;

interface Props extends Omit<IUpdateFormikReturn<Plan | Package>, 'formik'> {
  type: ProductTypes;
  buttonDisabled?: boolean;
  product?: Package | Plan;
  disableSave?: boolean
}

export const AdminUpdateBreadcrumb = ({
  type,
  disableToggle,
  disabled,
  buttonDisabled,
  isLoading,
  product,
  disableSave
}: Props) => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { product: stateProduct } = (state as LocationCloneState) || {};
  const { t } = useTranslation();
  const { name, productFamily } = useParams();
  const backLink = useMemo(
    () =>
      `${routesPaths.ADMIN_PRODUCTS.replace(
        ':productFamily',
        productFamily as string
      )}?tab=${type}`,
    [type, productFamily]
  );

  const handleBackLinkClick = useCallback(() => {
    if (window.history.length <= 2) {
      navigate(backLink);
    } else {
      navigate(-1);
    }
  }, [navigate, backLink]);

  const handleClone = useCallback(() => {
    const clonedName = `${(product?.name || name)}_clone_${moment().toISOString()}`;
    const clonedProduct: Package | Plan = { ...(product || {}) };
    delete clonedProduct['__v'];
    delete clonedProduct['_id'];
    disableToggle();
    navigate(
      routesPaths.ADMIN_PRODUCT_UPDATE
        .replace(':productFamily', productFamily as string)
        .replace(':type', type)
        .replace(':name', clonedName),
      {
        state: {
          product: {
            ...clonedProduct,
            name: clonedName,
          }
        }
      },
    );
  }, [product]);

  return (
    <StyledContainer>
      <StyledBackLinkContainer>
        <StyledBackLink onClick={handleBackLinkClick}>
          {t(`admin.${type}-title`)}
        </StyledBackLink>
        <StyledPackageName>{` / ${name}`}</StyledPackageName>
      </StyledBackLinkContainer>
      <StyledButtonsContainer>
        {type === productType.PACKAGES && !stateProduct && (
          <StyledButton
            isBorderless
            withoutMargin
            type="button"
            onClick={handleClone}
            disabled={isLoading || buttonDisabled}
            loading={isLoading}
          >
            {t('common.clone')}
          </StyledButton>
        )}
        <StyledButton
          withoutMargin
          type={disabled ? 'button' : 'submit'}
          onClick={disabled ? disableToggle : undefined}
          disabled={disableSave || (isLoading || buttonDisabled)}
          loading={isLoading}
        >
          {t(`common.${disabled ? 'edit' : 'save'}`)}
        </StyledButton>
      </StyledButtonsContainer>
    </StyledContainer>
  );
};
